import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import {
  FormValues,
  MAXIMIZE_DONATIONS_TOOLTIP_CONTENT,
  STEP_ID,
  validation,
} from './DonationAmount.meta';
import { INPUTS_WIDTH } from '../../CharitableGiving.meta';
import { useNavigation } from '../../hooks/useNavigation';
import { InputNumberMask } from '../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { GivingStrategy } from '../../api/useCharitableGivingQuery';
import { Checkbox } from '../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';

export const DonationAmount = () => {
  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const canMaximizeDonations =
    workflow.data.givingStrategy === GivingStrategy.QualifiedCharitableDonations;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      donationAmount: workflow.data.donationAmount,
      maximizeDonations: (canMaximizeDonations && workflow.data.maximizeDonations) || false,
    },
    resolver: yupResolver(validation),
  });

  const onSubmit = handleSubmit(values => {
    const newWorkflow = {
      ...workflow,
      data: {
        ...workflow.data,
        donationAmount: values.donationAmount,
        maximizeDonations: values.maximizeDonations,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const maximizeDonations = watch('maximizeDonations');

  return (
    <StepContainer
      title="How much would you like to donate?"
      onSubmit={onSubmit}
      footer={
        <StepFooter
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
        />
      }
    >
      <>
        <Controller
          control={control}
          name="donationAmount"
          render={({ field: { value, onChange, ref } }) => (
            <Box width={INPUTS_WIDTH}>
              <InputNumberMask
                ref={ref}
                label="Donation Amount"
                leftIcon="fa fa-dollar-sign"
                innerText="/annually"
                value={value ?? ''}
                disabled={maximizeDonations}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                formVariant={errors.donationAmount ? 'error' : undefined}
                helperText={errors.donationAmount?.message}
              />
            </Box>
          )}
        />

        {canMaximizeDonations && (
          <Box display="flex" alignItems="center" mt="32">
            <Checkbox
              {...register('maximizeDonations', {
                onChange: evt => {
                  if (evt.target.checked) {
                    setValue('donationAmount', null, { shouldDirty: true, shouldValidate: true });
                  }
                },
              })}
            >
              Maximize Donations
            </Checkbox>

            <Tooltip content={MAXIMIZE_DONATIONS_TOOLTIP_CONTENT}>
              <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" />
            </Tooltip>
          </Box>
        )}
      </>
    </StepContainer>
  );
};
